import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSchoolName } from "./schoolNameStore";

// ─────────────────────────────────────────────────────────────────────────────
//  DEPARTMENT CONFIG
// ─────────────────────────────────────────────────────────────────────────────
import { ArrowRight } from "lucide-react";
const DEPT_SUBJECTS = {
  Maths: [],
  Science: [],
  Tamil: [],
  English: [],
  Social: [],
  Admin: [],
  "Bio Maths": ["Mathematics", "Physics", "Chemistry", "Biology", "Computer Science"],
  "Computer Science": ["Computer Science", "Mathematics", "Physics", "Chemistry", "Commerce"],
  "Art & Science": ["History", "Geography", "Economics", "Political Science", "Sociology", "Tamil", "English", "French"],
  "Commerce": ["Accountancy", "Business Maths", "Economics", "Commerce", "Computer Applications", "English"]
};
const DEPTS = ["All Staff", "Maths", "Science", "Tamil", "English", "Social", "Admin", "Bio Maths", "Computer Science", "Art & Science", "Commerce", "Hostel"];
const DEPT_COLORS = {
  Maths: "#0D47A1",
  Science: "#0D47A1",
  Tamil: "#0D47A1",
  English: "#0D47A1",
  Social: "#0D47A1",
  Admin: "#0D47A1",
  "Bio Maths": "#0D47A1",
  "Computer Science": "#0D47A1",
  "Art & Science": "#0D47A1",
  "Commerce": "#0D47A1",
  "Hostel": "#0D47A1"
};
const AV_COLORS = {
  Maths: {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  Science: {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  Tamil: {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  English: {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  Social: {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  Admin: {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  "Bio Maths": {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  "Computer Science": {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  "Art & Science": {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  "Commerce": {
    bg: "#FFFFFF",
    color: "#0D47A1"
  },
  "Hostel": {
    bg: "#FFFFFF",
    color: "#0D47A1"
  }
};
const SUBJ_BADGE = {
  "Bio Maths": {
    bg: "#0D47A1",
    color: "#FFFFFF"
  },
  "Computer Science": {
    bg: "#0D47A1",
    color: "#FFFFFF"
  },
  "Art & Science": {
    bg: "#0D47A1",
    color: "#FFFFFF"
  },
  "Commerce": {
    bg: "#0D47A1",
    color: "#FFFFFF"
  }
};

// ─────────────────────────────────────────────────────────────────────────────
//  CLASS / ADVISOR STRUCTURE
// ─────────────────────────────────────────────────────────────────────────────
const HS_GROUPS = ["Bio Maths", "Computer Science", "Art & Science", "Commerce"];
const generateAllClasses = () => {
  const list = [];
  ["UKG", "LKG"].forEach(kg => ["A", "B", "C"].forEach(s => list.push(`${kg}-${s}`)));
  for (let i = 1; i <= 10; i++) ["A", "B", "C"].forEach(s => list.push(`Class ${i}-${s}`));
  [11, 12].forEach(std => HS_GROUPS.forEach(group => ["A", "B"].forEach(sec => list.push(`Class ${std} ${group}-${sec}`))));
  return list;
};
const ALL_CLASSES = generateAllClasses();
const generateClassGroups = () => {
  const groups = [];
  ["UKG", "LKG"].forEach(kg => groups.push({
    label: kg,
    classes: ["A", "B", "C"].map(s => ({
      className: `${kg}-${s}`,
      eligibleDept: null
    }))
  }));
  for (let i = 1; i <= 10; i++) groups.push({
    label: `Class ${i}`,
    classes: ["A", "B", "C"].map(s => ({
      className: `Class ${i}-${s}`,
      eligibleDept: null
    }))
  });
  [11, 12].forEach(std => groups.push({
    label: `Class ${std}`,
    isHS: true,
    std,
    hsGroups: HS_GROUPS.map(group => ({
      group,
      classes: ["A", "B"].map(sec => ({
        className: `Class ${std} ${group}-${sec}`,
        eligibleDept: group
      }))
    }))
  }));
  return groups;
};
const CLASS_GROUPS = generateClassGroups();

// ─────────────────────────────────────────────────────────────────────────────
//  SHARED STYLES
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: "",
  role: "",
  dept: "Maths",
  subjects: [],
  exp: "",
  phone: "",
  email: "",
  qual: "",
  join: "",
  status: "present",
  username: "",
  password: "",
  salary: 0
};
const iBase = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'Poppins', sans-serif",
  outline: "none",
  background: "#FFFFFF",
  color: "#0D47A1",
  boxSizing: "border-box",
  transition: "all 0.2s",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
};
const iNormal = {
  ...iBase
};
const iError = {
  ...iBase,
  boxShadow: "0 0 0 2px #dc2626"
};
function Field({
  label,
  error,
  children
}) {
  return <div>
      <label style={{
      fontSize: 12,
      fontWeight: 500,
      color: "#0D47A1",
      marginBottom: 5,
      display: "block"
    }}>{label}</label>
      {children}
      {error && <div style={{
      color: "#dc2626",
      fontSize: 11,
      marginTop: 3
    }}>{error}</div>}
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  PRINCIPAL DETAILS VIEW MODAL
// ─────────────────────────────────────────────────────────────────────────────
function PrincipalDetailsModal({
  onClose,
  onEdit
}) {
  const [hmData, setHmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPw, setShowPw] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/headmistress");
        if (r.ok) {
          const result = await r.json();
          setHmData(result.data || null);
        }
      } catch {/* keep null */} finally {
        setLoading(false);
      }
    })();
  }, []);
  const nameParts = (hmData?.name || "").trim().split(" ").filter(Boolean);
  const initials = nameParts.length >= 2 ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase() : (hmData?.name || "HM").slice(0, 2).toUpperCase();
  const rows = hmData ? [{
    label: "Full Name",
    value: hmData.name || "—",
    icon: "fa-user"
  }, {
    label: "Title / Role",
    value: hmData.title || "—",
    icon: "fa-id-badge"
  }, {
    label: "Institution",
    value: hmData.school || "—",
    icon: "fa-school"
  }, {
    label: "In Role Since",
    value: hmData.since || "—",
    icon: "fa-calendar-alt"
  }, {
    label: "Experience",
    value: hmData.exp ? `${hmData.exp} years` : "—",
    icon: "fa-briefcase"
  }, {
    label: "Qualification",
    value: hmData.qual || "—",
    icon: "fa-graduation-cap"
  }, {
    label: "Phone",
    value: hmData.phone || "—",
    icon: "fa-phone"
  }, {
    label: "Email",
    value: hmData.email || "—",
    icon: "fa-envelope"
  }, {
    label: "Office Address",
    value: hmData.address || "—",
    icon: "fa-map-marker-alt"
  }] : [];
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1100
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 20,
      width: 560,
      maxHeight: "92vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 32px 80px rgba(0,0,0,0.15)"
    }}>
        <div style={{
        background: "#0D47A1",
        padding: "18px 22px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 16,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
              <i className="fas fa-crown" style={{
              color: "#FFFFFF"
            }}></i> Principal Details
            </div>
            <div style={{
            fontSize: 11,
            color: "#FFFFFF",
            marginTop: 3
          }}>Stored profile information</div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          color: "#FFFFFF",
          width: 32,
          height: 32,
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {hmData && <div style={{
        background: "#E3F2FD",
        padding: "16px 22px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 14
      }}>
            <div style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: "#0D47A1",
          color: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          fontSize: 20,
          flexShrink: 0
        }}>{initials}</div>
            <div>
              <div style={{
            fontWeight: 700,
            fontSize: 15,
            color: "#0D47A1"
          }}>{hmData.name}</div>
              <div style={{
            fontSize: 11,
            color: "#0D47A1",
            marginTop: 2
          }}>
                {hmData.title}{hmData.school ? ` · ${hmData.school}` : ""}{hmData.since ? ` · Since ${hmData.since}` : ""}
              </div>
            </div>
          </div>}

        <div style={{
        padding: "20px 22px",
        overflowY: "auto",
        flex: 1
      }}>
          {loading ? <div style={{
          textAlign: "center",
          padding: "48px 0",
          color: "#0D47A1"
        }}>
              <i className="fas fa-spinner fa-pulse" style={{
            fontSize: 28,
            marginBottom: 12
          }}></i>
              <div style={{
            fontSize: 13
          }}>Loading principal details...</div>
            </div> : !hmData ? <div style={{
          textAlign: "center",
          padding: "48px 0",
          color: "#0D47A1"
        }}>
              <i className="fas fa-user-slash" style={{
            fontSize: 36,
            marginBottom: 14,
            display: "block"
          }}></i>
              <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#0D47A1",
            marginBottom: 6
          }}>No Principal Configured</div>
              <div style={{
            fontSize: 12,
            marginBottom: 20
          }}>Click "Edit Details" below to add principal information.</div>
              <button onClick={() => {
            onClose();
            onEdit();
          }} style={{
            background: "#0D47A1",
            color: "#FFFFFF",
            border: "none",
            padding: "10px 24px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            display: "inline-flex",
            alignItems: "center",
            gap: 6
          }}>
                <i className="fas fa-edit"></i> Add Details
              </button>
            </div> : <>
              <div style={{
            borderRadius: 12,
            overflow: "hidden",
            marginBottom: 16,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
                <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "'Poppins',sans-serif"
            }}>
                  <thead>
                    <tr style={{
                  background: "#0D47A1"
                }}>
                      <th style={{
                    padding: "10px 16px",
                    textAlign: "left",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#FFFFFF",
                    letterSpacing: "0.5px",
                    width: "36%"
                  }}>FIELD</th>
                      <th style={{
                    padding: "10px 16px",
                    textAlign: "left",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#FFFFFF",
                    letterSpacing: "0.5px"
                  }}>DETAILS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => <tr key={row.label} style={{
                  background: idx % 2 === 0 ? "#FFFFFF" : "#E3F2FD"
                }}>
                        <td style={{
                    padding: "11px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#0D47A1",
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}>
                          <i className={`fas ${row.icon}`} style={{
                      color: "#0D47A1",
                      width: 14,
                      textAlign: "center"
                    }}></i>
                          {row.label}
                        </td>
                        <td style={{
                    padding: "11px 16px",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#0D47A1",
                    wordBreak: "break-word"
                  }}>{row.value}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div style={{
            background: "#E3F2FD",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
                <div style={{
              padding: "10px 16px",
              background: "#0D47A1",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}>
                  <i className="fas fa-lock" style={{
                color: "#FFFFFF",
                fontSize: 12
              }}></i>
                  <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#FFFFFF",
                letterSpacing: "0.5px"
              }}>LOGIN CREDENTIALS</span>
                </div>
                <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "'Poppins',sans-serif"
            }}>
                  <tbody>
                    <tr>
                      <td style={{
                    padding: "11px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#0D47A1",
                    width: "36%"
                  }}>
                        <i className="fas fa-user" style={{
                      marginRight: 8
                    }}></i>Username
                      </td>
                      <td style={{
                    padding: "11px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#0D47A1",
                    fontFamily: "monospace"
                  }}>
                        {hmData.username || "—"}
                      </td>
                    </tr>
                    <tr style={{
                  background: "#E3F2FD"
                }}>
                      <td style={{
                    padding: "11px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#0D47A1"
                  }}>
                        <i className="fas fa-key" style={{
                      marginRight: 8
                    }}></i>Password
                      </td>
                      <td style={{
                    padding: "11px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#0D47A1",
                    fontFamily: "monospace"
                  }}>
                        <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8
                    }}>
                          {showPw ? hmData.passwordDisplay || "••••••••" : "••••••••"}
                          <button onClick={() => setShowPw(p => !p)} style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 14,
                        color: "#0D47A1",
                        padding: 0,
                        display: "inline-flex"
                      }}>
                            <i className={`fas ${showPw ? "fa-eye-slash" : "fa-eye"}`}></i>
                          </button>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{
            display: "flex",
            gap: 10,
            marginTop: 18
          }}>
                <button onClick={() => {
              onClose();
              onEdit();
            }} style={{
              flex: 1,
              background: "#0D47A1",
              color: "#FFFFFF",
              border: "none",
              padding: "10px 0",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'Poppins',sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6
            }}>
                  <i className="fas fa-edit"></i> Edit Details
                </button>
                <button onClick={onClose} style={{
              flex: 1,
              background: "#E3F2FD",
              color: "#0D47A1",
              border: "none",
              padding: "10px 0",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'Poppins',sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6
            }}>
                  <i className="fas fa-times"></i> Close
                </button>
              </div>
            </>}
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CO-ORDINATOR DETAILS MODAL
//  Left  : editable Co-ordinator form, card styled (saved to /api/coordinator)
//  Right : read-only Principal Details card (pulled from /api/headmistress)
// ─────────────────────────────────────────────────────────────────────────────
function CoordinatorModal({
  onClose,
  onSaved
}) {
  const EMPTY_COORD = {
    name: "",
    designation: "Academic Co-ordinator",
    department: "",
    phone: "",
    email: "",
    qual: "",
    exp: "",
    since: "",
    notes: ""
  };
  const [form, setForm] = useState(EMPTY_COORD);
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [coLoading, setCoLoading] = useState(true);
  const [hmData, setHmData] = useState(null);
  const [hmLoading, setHmLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/coordinator");
        if (r.ok) {
          const result = await r.json();
          if (result.data) setForm({
            ...EMPTY_COORD,
            ...result.data
          });
        }
      } catch {/* keep defaults */} finally {
        setCoLoading(false);
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/headmistress");
        if (r.ok) {
          const result = await r.json();
          setHmData(result.data || null);
        }
      } catch {/* keep null */} finally {
        setHmLoading(false);
      }
    })();
  }, []);
  const set = field => e => {
    const val = e && e.target ? e.target.value : e;
    setForm(prev => ({
      ...prev,
      [field]: val
    }));
    if (errors[field]) setErrors(er => ({
      ...er,
      [field]: null
    }));
    if (saved) setSaved(false);
    if (saveError) setSaveError("");
  };
  const handleSave = async () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/coordinator", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form)
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Save failed");
      if (result.data) {
        setForm({
          ...EMPTY_COORD,
          ...result.data
        });
        if (onSaved) onSaved(result.data);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveError(err.message || "Server error. Is backend running?");
    } finally {
      setSaving(false);
    }
  };
  const principalName = hmData?.name || "—";
  const principalParts = (hmData?.name || "").trim().split(" ").filter(Boolean);
  const principalInitials = principalParts.length >= 2 ? (principalParts[0][0] + principalParts[principalParts.length - 1][0]).toUpperCase() : (hmData?.name || "HM").slice(0, 2).toUpperCase();
  const principalRows = hmData ? [{
    icon: "fa-school",
    label: "Institution",
    value: hmData.school
  }, {
    icon: "fa-calendar-alt",
    label: "Since",
    value: hmData.since
  }, {
    icon: "fa-briefcase",
    label: "Experience",
    value: hmData.exp ? `${hmData.exp} years` : ""
  }, {
    icon: "fa-graduation-cap",
    label: "Qualification",
    value: hmData.qual
  }, {
    icon: "fa-phone",
    label: "Phone",
    value: hmData.phone
  }, {
    icon: "fa-envelope",
    label: "Email",
    value: hmData.email
  }].filter(r => r.value) : [];
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1100,
    padding: 16
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 20,
      width: "min(920px, 100%)",
      maxHeight: "92vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 32px 80px rgba(0,0,0,0.15)"
    }}>
        <div style={{
        background: "#0D47A1",
        padding: "18px 22px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 16,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
              <i className="fas fa-user-tie" style={{
              color: "#FFFFFF"
            }}></i> Co-ordinator Details
            </div>
            <div style={{
            fontSize: 11,
            color: "#FFFFFF",
            marginTop: 3
          }}>Add / update the school co-ordinator's information</div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          color: "#FFFFFF",
          width: 32,
          height: 32,
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div style={{
        padding: 22,
        overflowY: "auto",
        flex: 1,
        display: "grid",
        gridTemplateColumns: "1.15fr 0.85fr",
        gap: 22
      }} className="coord-modal-grid">
          {/* ── LEFT: Co-ordinator form ── */}
          <div>
            <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#0D47A1",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: 14,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
              <i className="fas fa-id-badge"></i> Co-ordinator Information
            </div>

            <div style={{
            background: "#E3F2FD",
            borderRadius: 16,
            padding: 18,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
              {coLoading ? <div style={{
              textAlign: "center",
              padding: "30px 0",
              color: "#0D47A1"
            }}>
                  <i className="fas fa-spinner fa-pulse" style={{
                fontSize: 22,
                marginBottom: 8
              }}></i>
                  <div style={{
                fontSize: 12
              }}>Loading...</div>
                </div> : <>
                  {saved && <div style={{
                background: "#FFFFFF",
                color: "#0D47A1",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                      <i className="fas fa-check-circle"></i> Co-ordinator details saved
                    </div>}
                  {saveError && <div style={{
                background: "#FFFFFF",
                color: "#C62828",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                      <i className="fas fa-exclamation-circle"></i> {saveError}
                    </div>}

                  <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12
              }}>
                    <div style={{
                  gridColumn: "1 / -1"
                }}>
                      <Field label="Full Name *" error={errors.name}>
                        <input style={errors.name ? iError : iNormal} value={form.name} onChange={set("name")} placeholder="e.g. Mrs. Kavitha Ramesh" />
                      </Field>
                    </div>
                    <Field label="Designation">
                      <input style={iNormal} value={form.designation} onChange={set("designation")} placeholder="e.g. Academic Co-ordinator" />
                    </Field>
                    <Field label="Department">
                      <input style={iNormal} value={form.department} onChange={set("department")} placeholder="e.g. Senior Secondary" />
                    </Field>
                    <Field label="Phone">
                      <input style={iNormal} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" />
                    </Field>
                    <Field label="Email">
                      <input style={iNormal} value={form.email} onChange={set("email")} placeholder="coordinator@school.edu.in" />
                    </Field>
                    <Field label="Qualification">
                      <input style={iNormal} value={form.qual} onChange={set("qual")} placeholder="e.g. M.Ed" />
                    </Field>
                    <Field label="Experience">
                      <input style={iNormal} value={form.exp} onChange={set("exp")} placeholder="e.g. 8 years" />
                    </Field>
                    <div style={{
                  gridColumn: "1 / -1"
                }}>
                      <Field label="In Role Since">
                        <input style={iNormal} value={form.since} onChange={set("since")} placeholder="e.g. 2021" />
                      </Field>
                    </div>
                    <div style={{
                  gridColumn: "1 / -1"
                }}>
                      <Field label="Notes">
                        <textarea style={{
                      ...iNormal,
                      resize: "vertical",
                      minHeight: 60
                    }} value={form.notes} onChange={set("notes")} placeholder="Any additional notes..." />
                      </Field>
                    </div>
                  </div>

                  <div style={{
                display: "flex",
                gap: 10,
                marginTop: 18
              }}>
                    <button onClick={handleSave} disabled={saving} style={{
                  flex: 1,
                  background: "#0D47A1",
                  color: "#FFFFFF",
                  border: "none",
                  padding: "10px 0",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: saving ? "default" : "pointer",
                  opacity: saving ? 0.7 : 1,
                  fontFamily: "'Poppins',sans-serif",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6
                }}>
                      <i className={`fas ${saving ? "fa-spinner fa-pulse" : "fa-save"}`}></i> {saving ? "Saving..." : "Save Details"}
                    </button>
                    <button onClick={onClose} style={{
                  flex: 1,
                  background: "#FFFFFF",
                  color: "#0D47A1",
                  border: "none",
                  padding: "10px 0",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'Poppins',sans-serif",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6
                }}>
                      <i className="fas fa-times"></i> Close
                    </button>
                  </div>
                </>}
            </div>
          </div>

          {/* ── RIGHT: Principal card ── */}
          <div>
            <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#0D47A1",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: 14,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
              <i className="fas fa-crown"></i> Principal Details
            </div>
            <div style={{
            background: "#E3F2FD",
            borderRadius: 16,
            padding: 18,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
              {hmLoading ? <div style={{
              textAlign: "center",
              padding: "30px 0",
              color: "#0D47A1"
            }}>
                  <i className="fas fa-spinner fa-pulse" style={{
                fontSize: 22,
                marginBottom: 8
              }}></i>
                  <div style={{
                fontSize: 12
              }}>Loading...</div>
                </div> : !hmData ? <div style={{
              textAlign: "center",
              padding: "30px 0",
              color: "#0D47A1"
            }}>
                  <i className="fas fa-user-slash" style={{
                fontSize: 26,
                marginBottom: 10,
                display: "block"
              }}></i>
                  <div style={{
                fontSize: 12
              }}>No principal configured yet</div>
                </div> : <>
                  <div style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16
              }}>
                    <div style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: "#0D47A1",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: 18,
                  flexShrink: 0
                }}>
                      {principalInitials}
                    </div>
                    <div>
                      <div style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#0D47A1"
                  }}>{principalName}</div>
                      <div style={{
                    fontSize: 11,
                    color: "#0D47A1",
                    marginTop: 2
                  }}>{hmData.title || "Headmistress"}</div>
                    </div>
                  </div>

                  <div style={{
                display: "flex",
                flexDirection: "column",
                gap: 10
              }}>
                    {principalRows.map(r => <div key={r.label} style={{
                  background: "#FFFFFF",
                  borderRadius: 10,
                  padding: "9px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10
                }}>
                        <i className={`fas ${r.icon}`} style={{
                    color: "#0D47A1",
                    width: 14,
                    textAlign: "center"
                  }}></i>
                        <div>
                          <div style={{
                      fontSize: 9,
                      color: "#0D47A1",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      fontWeight: 700
                    }}>{r.label}</div>
                          <div style={{
                      fontSize: 12,
                      color: "#0D47A1",
                      fontWeight: 600
                    }}>{r.value}</div>
                        </div>
                      </div>)}
                    {principalRows.length === 0 && <div style={{
                  fontSize: 12,
                  color: "#0D47A1",
                  textAlign: "center",
                  padding: "10px 0"
                }}>No further details on file</div>}
                  </div>
                </>}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 720px) {
          .coord-modal-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  HEADMISTRESS CONTACT MODAL
// ─────────────────────────────────────────────────────────────────────────────
function HeadmistressContactModal({
  onClose,
  onSaved
}) {
  const schoolName = useSchoolName();
  const [form, setForm] = useState({
    name: "",
    title: "Headmistress",
    school: `${schoolName} Group of Schools`,
    since: "",
    exp: "",
    qual: "",
    phone: "",
    email: "",
    address: "",
    username: "",
    password: ""
  });
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingHM, setLoadingHM] = useState(true);
  const set = (k, v) => setForm(p => ({
    ...p,
    [k]: v
  }));
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/headmistress");
        if (res.ok) {
          const result = await res.json();
          const d = result.data;
          if (d) {
            setForm({
              name: d.name || "",
              title: d.title || "Headmistress",
              school: d.school || `${schoolName} Group of Schools`,
              since: d.since || "",
              exp: d.exp || "",
              qual: d.qual || "",
              phone: d.phone || "",
              email: d.email || "",
              address: d.address || "",
              username: d.username || "",
              password: d.passwordDisplay || ""
            });
          }
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoadingHM(false);
      }
    };
    fetchData();
  }, []);
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone is required";
    if (!form.email.trim()) e.email = "Email is required";
    if (!form.username.trim()) e.username = "Username is required";
    if (!form.password.trim()) e.password = "Password is required";else if (form.password.length < 6) e.password = "Min 6 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setSaveError("");
    setSuccessMessage("");
    try {
      const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/headmistress", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form)
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Save failed");
      setSuccessMessage(result.message || "Principal details saved successfully!");
      setSaved(true);
      if (onSaved && result.data) onSaved(result.data);
      setTimeout(() => {
        setSaved(false);
        setSaving(false);
        onClose();
      }, 2000);
    } catch (err) {
      setSaveError(err.message || "Server error. Is backend running?");
      setSaving(false);
    }
  };
  const nameParts = form.name.trim().split(" ").filter(Boolean);
  const initials = nameParts.length >= 2 ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase() : form.name.slice(0, 2).toUpperCase() || "HM";
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1200
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 18,
      width: 560,
      maxHeight: "92vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 28px 72px rgba(0,0,0,0.15)"
    }}>
        <div style={{
        background: "#0D47A1",
        padding: "18px 22px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 16,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
              <i className="fas fa-crown" style={{
              color: "#FFFFFF"
            }}></i> Edit Principal Details
            </div>
            <div style={{
            fontSize: 11,
            color: "#FFFFFF",
            marginTop: 3
          }}>Update contact info &amp; login credentials</div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          color: "#FFFFFF",
          width: 32,
          height: 32,
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div style={{
        background: "#E3F2FD",
        padding: "16px 22px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 14
      }}>
          <div style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: "#0D47A1",
          color: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          fontSize: 20,
          flexShrink: 0
        }}>{initials}</div>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 15,
            color: "#0D47A1"
          }}>{form.name || "Headmistress"}</div>
            <div style={{
            fontSize: 11,
            color: "#0D47A1",
            marginTop: 2
          }}>
              {form.title}{form.school ? ` · ${form.school}` : ""}{form.since ? ` · Since ${form.since}` : ""}
            </div>
          </div>
        </div>

        <div style={{
        padding: "20px 22px",
        overflowY: "auto",
        flex: 1
      }}>
          {loadingHM ? <div style={{
          textAlign: "center",
          padding: "40px 0",
          color: "#0D47A1"
        }}>
              <i className="fas fa-spinner fa-pulse" style={{
            fontSize: 24,
            marginBottom: 10
          }}></i>
              <div style={{
            fontSize: 13
          }}>Loading details...</div>
            </div> : <>
              {successMessage && <div style={{
            background: "#0D47A1",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 16,
            color: "#FFFFFF",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
                  <i className="fas fa-check-circle"></i>
                  <span>{successMessage}</span>
                </div>}

              <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14
          }}>
                <div style={{
              gridColumn: "1/-1"
            }}>
                  <Field label="Full Name *" error={errors.name}>
                    <input style={errors.name ? iError : iNormal} value={form.name} placeholder="e.g. Dr. Kavitha Venkataraman" onChange={e => set("name", e.target.value)} />
                  </Field>
                </div>

                <Field label="Title / Designation">
                  <input style={iNormal} value={form.title} placeholder="e.g. Headmistress" onChange={e => set("title", e.target.value)} />
                </Field>

                <Field label="School / Institution">
                  <input style={iNormal} value={form.school} placeholder={`e.g. ${schoolName} Group of Schools`} onChange={e => set("school", e.target.value)} />
                </Field>

                <Field label="Experience (years)">
                  <input style={iNormal} type="number" min="0" value={form.exp} placeholder="e.g. 18" onChange={e => set("exp", e.target.value)} />
                </Field>

                <Field label="Qualification">
                  <input style={iNormal} value={form.qual} placeholder="e.g. M.Ed, PhD" onChange={e => set("qual", e.target.value)} />
                </Field>

                <Field label="In Role Since">
                  <input style={iNormal} value={form.since} placeholder="e.g. 2018" onChange={e => set("since", e.target.value)} />
                </Field>

                <Field label="Phone *" error={errors.phone}>
                  <input style={errors.phone ? iError : iNormal} value={form.phone} placeholder="98765 43210" onChange={e => set("phone", e.target.value)} />
                </Field>

                <div style={{
              gridColumn: "1/-1"
            }}>
                  <Field label="Email *" error={errors.email}>
                    <input style={errors.email ? iError : iNormal} type="email" value={form.email} placeholder={`headmistress@${schoolName.toLowerCase().replace(/[^a-z0-9]/g, "")}.edu`} onChange={e => set("email", e.target.value)} />
                  </Field>
                </div>

                <div style={{
              gridColumn: "1/-1"
            }}>
                  <Field label="Office Address">
                    <textarea style={{
                  ...iNormal,
                  resize: "none",
                  height: 68
                }} value={form.address} placeholder="School address..." onChange={e => set("address", e.target.value)} />
                  </Field>
                </div>

                <div style={{
              gridColumn: "1/-1"
            }}>
                  <div style={{
                background: "#E3F2FD",
                borderRadius: 10,
                padding: "14px 16px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }}>
                    <div style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#0D47A1",
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 6
                }}>
                      <i className="fas fa-lock"></i> Login Credentials
                    </div>
                    <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12
                }}>
                      <Field label="Username *" error={errors.username}>
                        <input style={errors.username ? iError : iNormal} value={form.username} placeholder="e.g. HM-001" onChange={e => set("username", e.target.value)} />
                      </Field>
                      <Field label="Password *" error={errors.password}>
                        <div style={{
                      position: "relative"
                    }}>
                          <input style={{
                        ...(errors.password ? iError : iNormal),
                        paddingRight: 36
                      }} type={showPw ? "text" : "password"} value={form.password} placeholder="Min 6 characters" onChange={e => set("password", e.target.value)} />
                          <button onClick={() => setShowPw(p => !p)} style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 14,
                        color: "#0D47A1"
                      }}>
                            <i className={`fas ${showPw ? "fa-eye-slash" : "fa-eye"}`}></i>
                          </button>
                        </div>
                      </Field>
                    </div>
                  </div>
                </div>
              </div>

              {saveError && <div style={{
            background: "#E3F2FD",
            borderRadius: 8,
            padding: "10px 14px",
            color: "#dc2626",
            fontSize: 12,
            marginTop: 14,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
                  <i className="fas fa-exclamation-triangle"></i> {saveError}
                </div>}
            </>}

          <div style={{
          display: "flex",
          gap: 10,
          marginTop: 20
        }}>
            <button onClick={handleSave} disabled={saving || loadingHM} style={{
            flex: 1,
            border: "none",
            padding: "11px 0",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: saving || loadingHM ? "not-allowed" : "pointer",
            fontFamily: "'Poppins',sans-serif",
            background: saved ? "#0D47A1" : saving ? "#E3F2FD" : "#0D47A1",
            color: saved ? "#FFFFFF" : saving ? "#0D47A1" : "#FFFFFF",
            transition: "background 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}>
              {saving ? <><i className="fas fa-spinner fa-pulse"></i> Saving...</> : saved ? <><i className="fas fa-check-circle"></i> Saved!</> : <><i className="fas fa-save"></i> Save Details</>}
            </button>
            <button onClick={onClose} style={{
            flex: 1,
            background: "#E3F2FD",
            color: "#0D47A1",
            border: "none",
            padding: "11px 0",
            borderRadius: 8,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}>
              <i className="fas fa-times"></i> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  SUBJECT SELECTOR
// ─────────────────────────────────────────────────────────────────────────────
function SubjectSelector({
  dept,
  selected,
  onChange
}) {
  const available = DEPT_SUBJECTS[dept] || [];
  if (available.length === 0) return null;
  const badge = SUBJ_BADGE[dept] || {
    bg: "#0D47A1",
    color: "#FFFFFF"
  };
  const toggle = subj => selected.includes(subj) ? onChange(selected.filter(s => s !== subj)) : onChange([...selected, subj]);
  return <div style={{
    gridColumn: "1/-1",
    background: "#E3F2FD",
    borderRadius: 10,
    padding: "14px 16px"
  }}>
      <div style={{
      fontSize: 12,
      fontWeight: 700,
      color: "#0D47A1",
      marginBottom: 10,
      display: "flex",
      alignItems: "center",
      gap: 6
    }}>
        <i className="fas fa-book-open"></i> Subjects taught in {dept}
        <span style={{
        fontSize: 10,
        fontWeight: 400,
        marginLeft: 8,
        opacity: 0.7
      }}>(click to toggle)</span>
      </div>
      <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 8
    }}>
        {available.map(subj => {
        const active = selected.includes(subj);
        return <button key={subj} type="button" onClick={() => toggle(subj)} style={{
          padding: "5px 12px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "'Poppins', sans-serif",
          background: active ? "#0D47A1" : "#FFFFFF",
          color: active ? "#FFFFFF" : "#0D47A1",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          transition: "all 0.15s"
        }}>
              {active ? <i className="fas fa-check"></i> : ""} {subj}
            </button>;
      })}
      </div>
      {selected.length === 0 && <div style={{
      fontSize: 11,
      opacity: 0.6,
      marginTop: 8,
      color: "#0D47A1"
    }}>No subjects selected — please pick at least one.</div>}
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CLASS ADVISOR MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ClassAdvisorModal({
  staffList,
  advisorMap,
  onClose,
  onSave,
  onDelete
}) {
  const [localMap, setLocalMap] = useState({
    ...advisorMap
  });
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const toggle = label => setExpanded(p => ({
    ...p,
    [label]: !p[label]
  }));
  const assignedCount = Object.values(localMap).filter(Boolean).length;
  const pct = Math.round(assignedCount / ALL_CLASSES.length * 100);
  const CORE_DEPTS = ["Maths", "Science", "Tamil", "English", "Social", "Admin"];
  const eligibleStaff = eligibleDept => !eligibleDept ? staffList.filter(s => CORE_DEPTS.includes(s.dept)) : staffList.filter(s => s.dept === eligibleDept);
  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave(localMap);
    if (ok) {
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        setSaving(false);
        onClose();
      }, 900);
    } else setSaving(false);
  };
  const handleDelete = async cls => {
    if (!window.confirm(`Remove advisor from ${cls}?`)) return;
    const m = {
      ...localMap
    };
    delete m[cls];
    setLocalMap(m);
    if (onDelete) await onDelete(cls);
  };
  const searchLower = search.toLowerCase();
  const visibleGroups = CLASS_GROUPS.filter(g => {
    if (!search) return true;
    if (g.isHS) return g.hsGroups.some(hg => hg.classes.some(c => c.className.toLowerCase().includes(searchLower)));
    return g.label.toLowerCase().includes(searchLower) || g.classes.some(c => c.className.toLowerCase().includes(searchLower));
  });
  const ClassRow = ({
    cls,
    idx
  }) => {
    const {
      className,
      eligibleDept
    } = cls;
    const assignedId = localMap[className];
    const assignedStaff = staffList.find(s => s._id === assignedId);
    const options = eligibleStaff(eligibleDept);
    return <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "9px 14px",
      background: idx % 2 === 0 ? "#FFFFFF" : "#E3F2FD"
    }}>
        <span style={{
        fontSize: 12,
        fontWeight: 600,
        color: "#0D47A1",
        width: 140,
        flexShrink: 0
      }}>{className}</span>
        <select value={assignedId || ""} onChange={e => setLocalMap(p => ({
        ...p,
        [className]: e.target.value || null
      }))} style={{
        flex: 1,
        padding: "6px 10px",
        borderRadius: 8,
        fontSize: 12,
        fontFamily: "'Poppins',sans-serif",
        outline: "none",
        background: assignedId ? "#0D47A1" : "#FFFFFF",
        color: assignedId ? "#FFFFFF" : "#0D47A1",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
      }}>
          <option value="">— Select class advisor —</option>
          {options.map(s => <option key={s._id} value={s._id}>{s.name} ({s.dept})</option>)}
        </select>
        {assignedStaff && <>
            <span style={{
          fontSize: 10,
          fontWeight: 600,
          flexShrink: 0,
          background: "#0D47A1",
          color: "#FFFFFF",
          borderRadius: 6,
          padding: "3px 8px"
        }}><i className="fas fa-check"></i></span>
            <button onClick={() => handleDelete(className)} style={{
          background: "#E3F2FD",
          color: "#dc2626",
          borderRadius: 6,
          padding: "4px 8px",
          fontSize: 11,
          cursor: "pointer",
          fontFamily: "'Poppins',sans-serif",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}><i className="fas fa-trash-alt"></i></button>
          </>}
      </div>;
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 20,
      width: 760,
      maxHeight: "92vh",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 32px 80px rgba(0,0,0,0.15)",
      overflow: "hidden"
    }}>
        <div style={{
        background: "#0D47A1",
        padding: "18px 22px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 16,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
              <i className="fas fa-chalkboard-user"></i> Class Advisor Allocation
            </div>
            <div style={{
            fontSize: 11,
            color: "#FFFFFF",
            marginTop: 3
          }}>{assignedCount} of {ALL_CLASSES.length} classes assigned — Group‑specific staff for 11 &amp; 12</div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          color: "#FFFFFF",
          width: 32,
          height: 32,
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}><i className="fas fa-times"></i></button>
        </div>

        <div style={{
        height: 4,
        background: "#E3F2FD",
        flexShrink: 0
      }}>
          <div style={{
          height: 4,
          width: `${pct}%`,
          background: "#0D47A1",
          transition: "width 0.3s"
        }} />
        </div>

        <div style={{
        padding: "12px 20px 0",
        flexShrink: 0
      }}>
          <div style={{
          position: "relative"
        }}>
            <i className="fas fa-search" style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#0D47A1"
          }}></i>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search class (e.g. Class 9, UKG, Bio Maths)..." style={{
            ...iNormal,
            paddingLeft: 32
          }} />
          </div>
        </div>

        <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "10px 20px 16px"
      }}>
          {visibleGroups.map(grp => {
          const isOpen = expanded[grp.label] || !!search;
          if (grp.isHS) {
            const allClasses = grp.hsGroups.flatMap(hg => hg.classes);
            const stdAssigned = allClasses.filter(c => localMap[c.className]).length;
            return <div key={grp.label} style={{
              marginBottom: 8
            }}>
                  <div onClick={() => toggle(grp.label)} style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "9px 14px",
                background: "#E3F2FD",
                borderRadius: isOpen ? "10px 10px 0 0" : 10,
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }}>
                    <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10
                }}>
                      <span style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: "#0D47A1"
                  }}>{grp.label}</span>
                      <span style={{
                    fontSize: 10,
                    background: "#0D47A1",
                    color: "#FFFFFF",
                    borderRadius: 4,
                    padding: "2px 7px"
                  }}>Higher Secondary · 4 Groups</span>
                      <span style={{
                    fontSize: 10,
                    fontWeight: 500,
                    borderRadius: 4,
                    padding: "2px 6px",
                    background: stdAssigned === allClasses.length ? "#0D47A1" : "#E3F2FD",
                    color: stdAssigned === allClasses.length ? "#FFFFFF" : "#0D47A1"
                  }}>{stdAssigned}/{allClasses.length} assigned</span>
                    </div>
                    <span style={{
                  color: "#0D47A1",
                  fontSize: 12
                }}>{isOpen ? <i className="fas fa-chevron-up"></i> : <i className="fas fa-chevron-down"></i>}</span>
                  </div>
                  {isOpen && <div style={{
                borderRadius: "0 0 10px 10px",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }}>
                      {grp.hsGroups.map((hg, hgIdx) => {
                  const badge = SUBJ_BADGE[hg.group] || {
                    bg: "#0D47A1",
                    color: "#FFFFFF"
                  };
                  const groupAssigned = hg.classes.filter(c => localMap[c.className]).length;
                  return <div key={hg.group}>
                            <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 14px",
                      background: badge.bg
                    }}>
                              <i className="fas fa-layer-group"></i>
                              <span style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: badge.color
                      }}>{hg.group}</span>
                              <span style={{
                        fontSize: 10,
                        color: badge.color,
                        opacity: 0.7
                      }}><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> Only {hg.group} staff shown</span>
                              <span style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        fontWeight: 600,
                        background: "#FFFFFF",
                        color: "#0D47A1",
                        borderRadius: 4,
                        padding: "1px 6px"
                      }}>{groupAssigned}/{hg.classes.length}</span>
                            </div>
                            {hg.classes.map((cls, idx) => <ClassRow key={cls.className} cls={cls} idx={idx} />)}
                          </div>;
                })}
                    </div>}
                </div>;
          }
          const filteredClasses = grp.classes.filter(c => !search || c.className.toLowerCase().includes(searchLower));
          if (filteredClasses.length === 0) return null;
          const stdAssigned = filteredClasses.filter(c => localMap[c.className]).length;
          return <div key={grp.label} style={{
            marginBottom: 8
          }}>
                <div onClick={() => toggle(grp.label)} style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "9px 14px",
              background: "#E3F2FD",
              borderRadius: isOpen ? "10px 10px 0 0" : 10,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                  <div style={{
                display: "flex",
                alignItems: "center",
                gap: 10
              }}>
                    <span style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#0D47A1"
                }}>{grp.label}</span>
                    <span style={{
                  fontSize: 10,
                  fontWeight: 500,
                  borderRadius: 4,
                  padding: "2px 6px",
                  background: stdAssigned === filteredClasses.length ? "#0D47A1" : "#E3F2FD",
                  color: stdAssigned === filteredClasses.length ? "#FFFFFF" : "#0D47A1"
                }}>{stdAssigned}/{filteredClasses.length} assigned</span>
                  </div>
                  <span style={{
                color: "#0D47A1",
                fontSize: 12
              }}>{isOpen ? <i className="fas fa-chevron-up"></i> : <i className="fas fa-chevron-down"></i>}</span>
                </div>
                {isOpen && <div style={{
              borderRadius: "0 0 10px 10px",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                    {filteredClasses.map((cls, idx) => <ClassRow key={cls.className} cls={cls} idx={idx} />)}
                  </div>}
              </div>;
        })}
        </div>

        <div style={{
        padding: "12px 20px",
        display: "flex",
        gap: 10,
        flexShrink: 0,
        background: "#E3F2FD"
      }}>
          <button onClick={handleSave} disabled={saving} style={{
          flex: 1,
          border: "none",
          padding: "10px 0",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 600,
          cursor: saving ? "not-allowed" : "pointer",
          fontFamily: "'Poppins',sans-serif",
          background: saved ? "#0D47A1" : saving ? "#E3F2FD" : "#0D47A1",
          color: saved ? "#FFFFFF" : saving ? "#0D47A1" : "#FFFFFF",
          transition: "background 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6
        }}>
            {saving ? <i className="fas fa-spinner fa-pulse"></i> : saved ? <i className="fas fa-check-circle"></i> : <i className="fas fa-save"></i>} {saving ? "Saving..." : saved ? "Saved!" : "Save All Allocations"}
          </button>
          <button onClick={onClose} style={{
          flex: 1,
          background: "#FFFFFF",
          color: "#0D47A1",
          border: "none",
          padding: "10px 0",
          borderRadius: 10,
          fontSize: 13,
          cursor: "pointer",
          fontFamily: "'Poppins',sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6
        }}>
            <i className="fas fa-times"></i> Cancel
          </button>
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  ADD / EDIT STAFF MODAL (UPDATED with Salary Field)
// ─────────────────────────────────────────────────────────────────────────────
function AddStaffModal({
  onClose,
  onAdd,
  onUpdate,
  editingStaff
}) {
  const [form, setForm] = useState(editingStaff ? {
    ...editingStaff,
    subjects: editingStaff.subjects || []
  } : EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const set = (k, v) => setForm(p => ({
    ...p,
    [k]: v
  }));
  const changeDept = dept => setForm(p => ({
    ...p,
    dept,
    subjects: []
  }));
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.role.trim()) e.role = "Role is required";
    if (!form.phone.trim()) e.phone = "Phone is required";
    if (!form.email.trim()) e.email = "Email is required";
    if (!form.qual.trim()) e.qual = "Qualification is required";
    if (!form.exp) e.exp = "Experience is required";
    if (DEPT_SUBJECTS[form.dept]?.length > 0 && form.subjects.length === 0) e.subjects = "Select at least one subject";
    if (!editingStaff && !form.username.trim()) e.username = "Username is required";
    if (!form.password.trim()) e.password = "Password is required";else if (form.password.length < 6) e.password = "Min 6 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      let res;
      if (editingStaff) {
        res = await fetch(`https://schoolcrm-fzcv.onrender.com/api/staff/${editingStaff._id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(form)
        });
      } else {
        res = await fetch("https://schoolcrm-fzcv.onrender.com/api/staff", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(form)
        });
      }
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data.message || "Failed to save");
        setSubmitting(false);
        return;
      }
      editingStaff ? onUpdate(data) : onAdd(data);
      onClose();
    } catch {
      setSubmitError("Server error. Is backend running?");
      setSubmitting(false);
    }
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 16,
      width: 580,
      maxHeight: "92vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 24px 64px rgba(0,0,0,0.15)"
    }}>
        <div style={{
        background: "#0D47A1",
        padding: "18px 22px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 16,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
              <i className="fas fa-user-plus"></i> {editingStaff ? "Edit Staff" : "Add New Staff"}
            </div>
            <div style={{
            fontSize: 11,
            color: "#FFFFFF",
            marginTop: 3
          }}>{editingStaff ? "Update staff information" : "Fill in all details & set login credentials"}</div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          color: "#FFFFFF",
          width: 32,
          height: 32,
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}><i className="fas fa-times"></i></button>
        </div>

        <div style={{
        padding: "22px 24px",
        overflowY: "auto",
        flex: 1
      }}>
          <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16
        }}>
            <div style={{
            gridColumn: "1/-1"
          }}>
              <Field label="Full Name *" error={errors.name}>
                <input style={errors.name ? iError : iNormal} value={form.name} placeholder="e.g. Mrs. Anitha Kumar" onChange={e => set("name", e.target.value)} />
              </Field>
            </div>
            <div style={{
            gridColumn: "1/-1"
          }}>
              <Field label="Role / Subject & Class *" error={errors.role}>
                <input style={errors.role ? iError : iNormal} value={form.role} placeholder="e.g. Maths Teacher | Class 9, 10" onChange={e => set("role", e.target.value)} />
              </Field>
            </div>
            <Field label="Department *" error={errors.dept}>
              <select style={iNormal} value={form.dept} onChange={e => changeDept(e.target.value)}>
                <optgroup label="Core Departments">
                  {["Maths", "Science", "Tamil", "English", "Social", "Admin"].map(d => <option key={d}>{d}</option>)}
                </optgroup>
                <optgroup label="Higher Secondary Groups">
                  {["Bio Maths", "Computer Science", "Art & Science", "Commerce"].map(d => <option key={d}>{d}</option>)}
                </optgroup>
              </select>
            </Field>
            <Field label="Experience (years) *" error={errors.exp}>
              <input style={errors.exp ? iError : iNormal} type="number" min="0" value={form.exp} placeholder="e.g. 8" onChange={e => set("exp", e.target.value)} />
            </Field>
            {DEPT_SUBJECTS[form.dept]?.length > 0 && <div style={{
            gridColumn: "1/-1"
          }}>
                <SubjectSelector dept={form.dept} selected={form.subjects} onChange={v => set("subjects", v)} />
                {errors.subjects && <div style={{
              color: "#dc2626",
              fontSize: 11,
              marginTop: 4
            }}>{errors.subjects}</div>}
              </div>}
            <Field label="Phone *" error={errors.phone}>
              <input style={errors.phone ? iError : iNormal} value={form.phone} placeholder="98765 43210" onChange={e => set("phone", e.target.value)} />
            </Field>
            <Field label="Email *" error={errors.email}>
              <input style={errors.email ? iError : iNormal} type="email" value={form.email} placeholder="name@school.com" onChange={e => set("email", e.target.value)} />
            </Field>
            <Field label="Qualification *" error={errors.qual}>
              <input style={errors.qual ? iError : iNormal} value={form.qual} placeholder="e.g. M.Sc, B.Ed" onChange={e => set("qual", e.target.value)} />
            </Field>
            <Field label="Year Joined">
              <input style={iNormal} value={form.join} placeholder="e.g. 2021" onChange={e => set("join", e.target.value)} />
            </Field>
            
            {/* Salary Field */}
            <Field label="Monthly Salary (₹)">
              <input style={iNormal} type="number" min="0" step="100" value={form.salary || ''} placeholder="e.g. 25000" onChange={e => set("salary", e.target.value ? parseFloat(e.target.value) : 0)} />
              <div style={{
              fontSize: 10,
              color: "#0D47A1",
              marginTop: 4
            }}>
                Used for attendance-based salary calculations
              </div>
            </Field>
            
            <div style={{
            gridColumn: "1/-1"
          }}>
              <div style={{
              background: "#E3F2FD",
              borderRadius: 10,
              padding: "14px 16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#0D47A1",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 6
              }}>
                  <i className="fas fa-lock"></i> Login Credentials
                </div>
                <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12
              }}>
                  <Field label="Username" error={errors.username}>
                    <input style={errors.username ? iError : iNormal} value={form.username} placeholder="e.g. STF-1043" onChange={e => set("username", e.target.value)} disabled={!!editingStaff} />
                    {editingStaff && <div style={{
                    fontSize: 10,
                    color: "#0D47A1",
                    marginTop: 3
                  }}>Cannot be changed</div>}
                  </Field>
                  <Field label={editingStaff ? "New Password (optional)" : "Password *"} error={errors.password}>
                    <div style={{
                    position: "relative"
                  }}>
                      <input style={{
                      ...(errors.password ? iError : iNormal),
                      paddingRight: 36
                    }} type={showPw ? "text" : "password"} value={form.password} placeholder="Min 6 characters" onChange={e => set("password", e.target.value)} />
                      <button onClick={() => setShowPw(p => !p)} style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 14,
                      color: "#0D47A1"
                    }}>
                        <i className={`fas ${showPw ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </button>
                    </div>
                  </Field>
                </div>
              </div>
            </div>
          </div>

          {submitError && <div style={{
          background: "#E3F2FD",
          borderRadius: 8,
          padding: "10px 14px",
          color: "#dc2626",
          fontSize: 12,
          marginTop: 14,
          display: "flex",
          alignItems: "center",
          gap: 6
        }}>
              <i className="fas fa-exclamation-triangle"></i> {submitError}
            </div>}

          <div style={{
          display: "flex",
          gap: 10,
          marginTop: 22
        }}>
            <button onClick={handleSubmit} disabled={submitting} style={{
            flex: 1,
            background: submitting ? "#E3F2FD" : "#0D47A1",
            color: submitting ? "#0D47A1" : "#FFFFFF",
            border: "none",
            padding: "11px 0",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: submitting ? "not-allowed" : "pointer",
            fontFamily: "'Poppins',sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}>
              {submitting ? <i className="fas fa-spinner fa-pulse"></i> : <i className="fas fa-save"></i>} {submitting ? "Saving..." : editingStaff ? "Update Staff" : "Add Staff Member"}
            </button>
            <button onClick={onClose} style={{
            flex: 1,
            background: "#E3F2FD",
            color: "#0D47A1",
            border: "none",
            padding: "11px 0",
            borderRadius: 8,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}>
              <i className="fas fa-times"></i> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  ADD WARDEN MODAL — simplified, dedicated form to create Hostel Warden
//  login credentials (name, hostel, phone, username, password). Saves to the
//  SAME Staff collection via POST /api/staff (dept: "Hostel"), so it shows up
//  in the staff list/DB just like any other staff member.
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_WARDEN_FORM = {
  name: "",
  hostelType: "Boys Hostel",
  phone: "",
  salary: "",
  address: "",
  qual: "",
  username: "",
  password: ""
};
function AddWardenModal({
  onClose,
  onAdd
}) {
  const [form, setForm] = useState(EMPTY_WARDEN_FORM);
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const set = (k, v) => setForm(p => ({
    ...p,
    [k]: v
  }));
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone is required";
    if (!form.salary.toString().trim()) e.salary = "Salary is required";else if (Number(form.salary) <= 0) e.salary = "Enter a valid salary amount";
    if (!form.username.trim()) e.username = "Username is required";
    if (!form.password.trim()) e.password = "Password is required";else if (form.password.length < 6) e.password = "Min 6 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/staff", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: form.name,
          username: form.username,
          password: form.password,
          role: `${form.hostelType} Warden`,
          dept: "Hostel",
          subjects: [],
          phone: form.phone,
          salary: form.salary,
          address: form.address,
          qual: form.qual,
          status: "present"
        })
      });
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data.message || "Failed to save");
        setSubmitting(false);
        return;
      }
      onAdd(data);
      onClose();
    } catch {
      setSubmitError("Server error. Is backend running?");
      setSubmitting(false);
    }
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 16,
      width: 480,
      maxHeight: "92vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 24px 64px rgba(0,0,0,0.15)"
    }}>
        <div style={{
        background: "#0D47A1",
        padding: "18px 22px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 16,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
              <i className="fas fa-user-shield"></i> Add Hostel Warden
            </div>
            <div style={{
            fontSize: 11,
            color: "#FFFFFF",
            marginTop: 3
          }}>Create warden login credentials</div>
          </div>
          <button onClick={onClose} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          color: "#FFFFFF",
          width: 32,
          height: 32,
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}><i className="fas fa-times"></i></button>
        </div>

        <div style={{
        padding: "22px 24px",
        overflowY: "auto",
        flex: 1
      }}>
          <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16
        }}>
            <div style={{
            gridColumn: "1/-1"
          }}>
              <Field label="Full Name *" error={errors.name}>
                <input style={errors.name ? iError : iNormal} value={form.name} placeholder="e.g. Mr. Selvam" onChange={e => set("name", e.target.value)} />
              </Field>
            </div>
            <div style={{
            gridColumn: "1/-1"
          }}>
              <Field label="Hostel *">
                <select style={iNormal} value={form.hostelType} onChange={e => set("hostelType", e.target.value)}>
                  <option value="Boys Hostel">Boys Hostel</option>
                  <option value="Girls Hostel">Girls Hostel</option>
                </select>
              </Field>
            </div>
            <div style={{
            gridColumn: "1/-1"
          }}>
              <Field label="Phone *" error={errors.phone}>
                <input style={errors.phone ? iError : iNormal} value={form.phone} placeholder="98765 43210" onChange={e => set("phone", e.target.value)} />
              </Field>
            </div>
            <div>
              <Field label="Salary (₹) *" error={errors.salary}>
                <input style={errors.salary ? iError : iNormal} type="number" value={form.salary} placeholder="e.g. 25000" onChange={e => set("salary", e.target.value)} />
              </Field>
            </div>
            <div>
              <Field label="Qualification">
                <input style={iNormal} value={form.qual} placeholder="e.g. B.A." onChange={e => set("qual", e.target.value)} />
              </Field>
            </div>
            <div style={{
            gridColumn: "1/-1"
          }}>
              <Field label="Address">
                <input style={iNormal} value={form.address} placeholder="e.g. 12, Main Street, Chennai" onChange={e => set("address", e.target.value)} />
              </Field>
            </div>
            <div style={{
            gridColumn: "1/-1"
          }}>
              <div style={{
              background: "#E3F2FD",
              borderRadius: 10,
              padding: "14px 16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                <div style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#0D47A1",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 6
              }}>
                  <i className="fas fa-lock"></i> Login Credentials
                </div>
                <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12
              }}>
                  <Field label="Username" error={errors.username}>
                    <input style={errors.username ? iError : iNormal} value={form.username} placeholder="e.g. warden-boys" onChange={e => set("username", e.target.value)} />
                  </Field>
                  <Field label="Password *" error={errors.password}>
                    <div style={{
                    position: "relative"
                  }}>
                      <input style={{
                      ...(errors.password ? iError : iNormal),
                      paddingRight: 36
                    }} type={showPw ? "text" : "password"} value={form.password} placeholder="Min 6 characters" onChange={e => set("password", e.target.value)} />
                      <button onClick={() => setShowPw(p => !p)} style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 14,
                      color: "#0D47A1"
                    }}>
                        <i className={`fas ${showPw ? "fa-eye-slash" : "fa-eye"}`}></i>
                      </button>
                    </div>
                  </Field>
                </div>
              </div>
            </div>
          </div>

          {submitError && <div style={{
          background: "#E3F2FD",
          borderRadius: 8,
          padding: "10px 14px",
          color: "#dc2626",
          fontSize: 12,
          marginTop: 14,
          display: "flex",
          alignItems: "center",
          gap: 6
        }}>
              <i className="fas fa-exclamation-triangle"></i> {submitError}
            </div>}

          <div style={{
          display: "flex",
          gap: 10,
          marginTop: 22
        }}>
            <button onClick={handleSubmit} disabled={submitting} style={{
            flex: 1,
            background: submitting ? "#E3F2FD" : "#0D47A1",
            color: submitting ? "#0D47A1" : "#FFFFFF",
            border: "none",
            padding: "11px 0",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: submitting ? "not-allowed" : "pointer",
            fontFamily: "'Poppins',sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}>
              {submitting ? <i className="fas fa-spinner fa-pulse"></i> : <i className="fas fa-save"></i>} {submitting ? "Saving..." : "Add Warden"}
            </button>
            <button onClick={onClose} style={{
            flex: 1,
            background: "#E3F2FD",
            color: "#0D47A1",
            border: "none",
            padding: "11px 0",
            borderRadius: 8,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}>
              <i className="fas fa-times"></i> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  VIEW PROFILE MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ViewProfileModal({
  staff,
  onClose,
  onEdit,
  advisorMap
}) {
  const av = AV_COLORS[staff.dept] || {
    bg: "#FFFFFF",
    color: "#0D47A1"
  };
  const [showPw, setShowPw] = useState(false);
  const myClasses = ALL_CLASSES.filter(c => advisorMap[c] === staff._id);
  const subjBadge = SUBJ_BADGE[staff.dept];
  const subjects = staff.subjects || [];
  const fields = [["Department", staff.dept], ["Experience", `${staff.exp} years`], ["Phone", staff.phone], ["Email", staff.email], ["Qualification", staff.qual], ["Joined", staff.join || "—"], ["Status", staff.status === "present" ? "Present" : "On Leave"], ["Salary", staff.salary ? `₹${staff.salary.toLocaleString('en-IN')}` : "Not set"]];
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(13,71,161,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  }}>
      <div style={{
      background: "#FFFFFF",
      borderRadius: 16,
      width: 460,
      maxHeight: "92vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 24px 64px rgba(0,0,0,0.15)"
    }}>
        <div style={{
        height: 72,
        background: `linear-gradient(135deg, ${DEPT_COLORS[staff.dept] || "#0D47A1"}20, ${DEPT_COLORS[staff.dept] || "#0D47A1"}40)`,
        position: "relative",
        flexShrink: 0
      }}>
          <div style={{
          width: 60,
          height: 60,
          borderRadius: 14,
          background: av.bg,
          color: av.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 20,
          position: "absolute",
          bottom: -20,
          left: 20,
          border: "2px solid #FFFFFF",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
        }}>{staff.initials}</div>
          <button onClick={onClose} style={{
          position: "absolute",
          top: 12,
          right: 12,
          background: "rgba(0,0,0,0.2)",
          border: "none",
          color: "#FFFFFF",
          width: 28,
          height: 28,
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}><i className="fas fa-times"></i></button>
        </div>

        <div style={{
        padding: "28px 20px 20px",
        overflowY: "auto",
        flex: 1
      }}>
          <div style={{
          fontWeight: 700,
          fontSize: 17,
          color: "#0D47A1"
        }}>{staff.name}</div>
          <div style={{
          fontSize: 12,
          color: "#0D47A1",
          marginTop: 2
        }}>{staff.role}</div>

          {subjects.length > 0 && subjBadge && <div style={{
          marginTop: 12,
          background: subjBadge.bg,
          borderRadius: 10,
          padding: "10px 14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
              <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: subjBadge.color,
            marginBottom: 7,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
                <i className="fas fa-book-open"></i> SUBJECTS
              </div>
              <div style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap"
          }}>
                {subjects.map(s => <span key={s} style={{
              background: "#FFFFFF",
              color: "#0D47A1",
              fontWeight: 600,
              fontSize: 11,
              padding: "3px 10px",
              borderRadius: 6,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>{s}</span>)}
              </div>
            </div>}

          {myClasses.length > 0 && <div style={{
          marginTop: 12,
          background: "#E3F2FD",
          borderRadius: 10,
          padding: "10px 14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
              <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#0D47A1",
            marginBottom: 7,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
                <i className="fas fa-chalkboard"></i> CLASS ADVISOR FOR
              </div>
              <div style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap"
          }}>
                {myClasses.map(c => <span key={c} style={{
              background: "#FFFFFF",
              color: "#0D47A1",
              fontWeight: 600,
              fontSize: 11,
              padding: "3px 10px",
              borderRadius: 6,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>{c}</span>)}
              </div>
            </div>}

          <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginTop: 16
        }}>
            {fields.map(([k, v]) => <div key={k} style={{
            background: "#E3F2FD",
            borderRadius: 8,
            padding: "10px 12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
                <div style={{
              fontSize: 10,
              color: "#0D47A1",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 3
            }}>{k}</div>
                <div style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0D47A1"
            }}>{v}</div>
              </div>)}
          </div>

          <div style={{
          background: "#E3F2FD",
          borderRadius: 10,
          padding: "12px 14px",
          marginTop: 14,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
            <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#0D47A1",
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
              <i className="fas fa-lock"></i> Login Credentials
            </div>
            <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8
          }}>
              <div style={{
              background: "#FFFFFF",
              borderRadius: 8,
              padding: "8px 10px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                <div style={{
                fontSize: 10,
                color: "#0D47A1",
                marginBottom: 2
              }}>USERNAME</div>
                <div style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#0D47A1"
              }}>{staff.username || "—"}</div>
              </div>
              <div style={{
              background: "#FFFFFF",
              borderRadius: 8,
              padding: "8px 10px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                <div style={{
                fontSize: 10,
                color: "#0D47A1",
                marginBottom: 2
              }}>PASSWORD</div>
                <div style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#0D47A1",
                display: "flex",
                alignItems: "center",
                gap: 6
              }}>
                  <span>{showPw ? staff.passwordDisplay || "••••••••" : "••••••••"}</span>
                  <button onClick={() => setShowPw(p => !p)} style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: 0,
                  color: "#0D47A1"
                }}>
                    <i className={`fas ${showPw ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div style={{
          display: "flex",
          gap: 8,
          marginTop: 16
        }}>
            <button onClick={() => {
            onClose();
            onEdit(staff);
          }} style={{
            flex: 1,
            background: "#0D47A1",
            color: "#FFFFFF",
            border: "none",
            padding: 9,
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}>
              <i className="fas fa-edit"></i> Edit Staff
            </button>
            <button onClick={onClose} style={{
            flex: 1,
            background: "#E3F2FD",
            color: "#0D47A1",
            border: "none",
            padding: 9,
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "'Poppins',sans-serif",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6
          }}>
              <i className="fas fa-times"></i> Close
            </button>
          </div>
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function StaffManagement() {
  const schoolName = useSchoolName();
  const [staffList, setStaffList] = useState([]);
  const [advisorMap, setAdvisorMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeDept, setActiveDept] = useState("All Staff");
  const [showAdd, setShowAdd] = useState(false);
  const [showAddWarden, setShowAddWarden] = useState(false);
  const [viewStaff, setViewStaff] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showAdvisor, setShowAdvisor] = useState(false);
  const [showHMContact, setShowHMContact] = useState(false);
  const [showPrincipal, setShowPrincipal] = useState(false);
  const [showCoordinator, setShowCoordinator] = useState(false);
  const [hmData, setHmData] = useState({
    name: "",
    title: "Headmistress",
    school: "",
    since: "",
    exp: "",
    qual: ""
  });
  const [coData, setCoData] = useState(null);
  const navigate = useNavigate();
  const fetchStaff = async () => {
    try {
      const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/staff");
      if (!r.ok) throw new Error();
      setStaffList(await r.json());
    } catch {
      setStaffList([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchAdvisors = async () => {
    try {
      const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/class-advisors");
      if (!r.ok) throw new Error();
      const data = await r.json();
      const map = {};
      data.forEach(d => {
        if (d.staffId?._id) map[d.className] = d.staffId._id;else if (d.staffId) map[d.className] = d.staffId;
      });
      setAdvisorMap(map);
    } catch {
      setAdvisorMap({});
    }
  };
  const fetchHM = async () => {
    try {
      const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/headmistress");
      if (r.ok) {
        const result = await r.json();
        const d = result.data;
        if (d) {
          setHmData({
            name: d.name || "",
            title: d.title || "Headmistress",
            school: d.school || "",
            since: d.since || "",
            exp: d.exp || "",
            qual: d.qual || ""
          });
        }
      }
    } catch {/* keep defaults */}
  };
  const fetchCoordinator = async () => {
    try {
      const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/coordinator");
      if (r.ok) {
        const result = await r.json();
        setCoData(result.data || null);
      }
    } catch {/* keep null */}
  };
  useEffect(() => {
    fetchStaff();
    fetchAdvisors();
    fetchHM();
    fetchCoordinator();
  }, []);
  const handleCoordinatorSaved = savedData => setCoData(savedData);
  const handleHMSaved = savedData => {
    setHmData({
      name: savedData.name || "",
      title: savedData.title || "Headmistress",
      school: savedData.school || "",
      since: savedData.since || "",
      exp: savedData.exp || "",
      qual: savedData.qual || ""
    });
  };
  const handleSaveAdvisors = async map => {
    const payload = Object.entries(map).filter(([_, id]) => id && id !== "null" && id !== "").map(([className, staffId]) => ({
      className,
      staffId
    }));
    try {
      const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/class-advisors/bulk", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          advisors: payload
        })
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.message);
      }
      await fetchAdvisors();
      return true;
    } catch (e) {
      alert(e.message || "Save failed.");
      return false;
    }
  };
  const handleDeleteAdvisor = async className => {
    try {
      await fetch(`https://schoolcrm-fzcv.onrender.com/api/class-advisors/${encodeURIComponent(className)}`, {
        method: "DELETE"
      });
      await fetchAdvisors();
      return true;
    } catch {
      alert("Failed to delete advisor");
      return false;
    }
  };
  const handleUpdateStaff = updated => setStaffList(prev => prev.map(s => s._id === updated._id ? updated : s));
  const getAdvisedClasses = staffId => Object.entries(advisorMap).filter(([_, id]) => id === staffId).map(([c]) => c);
  const filtered = activeDept === "All Staff" ? staffList : staffList.filter(s => s.dept === activeDept);
  const assignedCount = Object.keys(advisorMap).length;
  if (loading) return <div style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    background: "#FFFFFF"
  }}>
      <div style={{
      textAlign: "center"
    }}>
        <i className="fas fa-spinner fa-pulse" style={{
        fontSize: 32,
        color: "#0D47A1",
        marginBottom: 12
      }}></i>
        <div style={{
        color: "#0D47A1",
        fontFamily: "'Poppins',sans-serif"
      }}>Loading staff...</div>
      </div>
    </div>;
  return <div style={{
    minHeight: "100vh",
    background: "#FFFFFF",
    fontFamily: "'Poppins', sans-serif"
  }}>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <style>{`* { box-sizing: border-box; }`}</style>

      {/* ── Header ── */}
      <div style={{
      background: "#0D47A1",
      padding: "16px 24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 16
      }}>
          <button onClick={() => navigate("/admin")} style={{
          background: "rgba(255,255,255,0.15)",
          border: "none",
          borderRadius: 10,
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#FFFFFF",
          fontSize: 18
        }} aria-label="Back to Admin">
            <i className="fas fa-arrow-left"></i>
          </button>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 18,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
              <i className="fas fa-chalkboard-user"></i> {schoolName} · Staff Management
            </div>
            <div style={{
            fontSize: 12,
            color: "#FFFFFF",
            marginTop: 2
          }}>Teachers, Admin &amp; Support Staff — Profiles &amp; Attendance</div>
          </div>
        </div>
        <nav style={{
        display: "flex",
        alignItems: "center",
        gap: 28,
        flexWrap: "wrap"
      }}>
          <span onClick={() => setShowPrincipal(true)} className="staff-nav-link">
            Principal
          </span>
          <span onClick={() => setShowCoordinator(true)} className="staff-nav-link">
            Co-ordinator
          </span>
          <span onClick={() => setShowAdd(true)} className="staff-nav-link">
            Add Staff
          </span>
          <span onClick={() => setShowAddWarden(true)} className="staff-nav-link">
            Add Warden
          </span>
          <span onClick={() => setShowAdvisor(true)} className="staff-nav-link">
            Class Advisor{assignedCount > 0 ? ` (${assignedCount})` : ""}
          </span>
        </nav>
      </div>
      <style>{`
        .staff-nav-link {
          color: #FFFFFF;
          font-size: 13px;
          font-weight: 500;
          font-family: 'Poppins', sans-serif;
          cursor: pointer;
          opacity: 0.9;
          padding-bottom: 2px;
          border-bottom: 1.5px solid transparent;
          transition: opacity 0.15s, border-color 0.15s;
        }
        .staff-nav-link:hover {
          opacity: 1;
          border-bottom-color: #FFFFFF;
        }
      `}</style>

      {/* ── Headmistress + Co-ordinator Banners ── */}
      <div style={{
      margin: "20px 24px 20px",
      display: "grid",
      gridTemplateColumns: coData ? "1fr 1fr" : "1fr",
      gap: 20
    }} className="hm-co-banner-grid">
        <div style={{
        background: "#E3F2FD",
        borderRadius: 16,
        padding: 20,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 20,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
      }}>
          <div style={{
          padding: "10px 14px",
          borderRadius: 12,
          background: "#0D47A1",
          color: "#FFFFFF",
          fontSize: 12,
          fontWeight: 700,
          textAlign: "center",
          flexShrink: 0
        }}>
            Principal
          </div>
          <div style={{
          flex: 1
        }}>
            <div style={{
            fontWeight: 700,
            fontSize: 18,
            color: "#0D47A1",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
              <i className="fas fa-crown"></i> {hmData.name || "—"}
            </div>
            <div style={{
            fontSize: 12,
            color: "#0D47A1",
            marginTop: 2
          }}>
              {hmData.title}{hmData.school ? ` — ${hmData.school}` : ""}{hmData.since ? ` | Since ${hmData.since}` : ""}
            </div>
            <div style={{
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            marginTop: 10
          }}>
              {[[hmData.exp ? `${hmData.exp} yrs` : "—", "Experience"], [hmData.qual || "—", "Qualification"], [staffList.length, "Staff under her"]].map(([v, l]) => <div key={l} style={{
              textAlign: "center"
            }}>
                  <div style={{
                fontWeight: 700,
                fontSize: 14,
                color: "#0D47A1"
              }}>{v}</div>
                  <div style={{
                fontSize: 10,
                color: "#0D47A1",
                marginTop: 1
              }}>{l}</div>
                </div>)}
            </div>
          </div>
          <div style={{
          display: "flex",
          gap: 8,
          flexShrink: 0
        }}>
           
            <button onClick={() => setShowHMContact(true)} style={{
            background: "#FFFFFF",
            color: "#0D47A1",
            padding: "6px 14px",
            borderRadius: 30,
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
              <i className="fas fa-edit"></i> Edit
            </button>
          </div>
        </div>

        {coData && <div style={{
        background: "#E3F2FD",
        borderRadius: 16,
        padding: 20,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 20,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
      }}>
            <div style={{
          padding: "10px 14px",
          borderRadius: 12,
          background: "#0D47A1",
          color: "#FFFFFF",
          fontSize: 12,
          fontWeight: 700,
          textAlign: "center",
          flexShrink: 0
        }}>
              Co-ordinator
            </div>
            <div style={{
          flex: 1
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 18,
            color: "#0D47A1",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>
                <i className="fas fa-user-tie"></i> {coData.name || "—"}
              </div>
              <div style={{
            fontSize: 12,
            color: "#0D47A1",
            marginTop: 2
          }}>
                {coData.designation || "Co-ordinator"}{coData.department ? ` — ${coData.department}` : ""}{coData.since ? ` | Since ${coData.since}` : ""}
              </div>
              <div style={{
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            marginTop: 10
          }}>
                {[[coData.exp || "—", "Experience"], [coData.qual || "—", "Qualification"], [coData.phone || "—", "Phone"]].map(([v, l]) => <div key={l} style={{
              textAlign: "center"
            }}>
                    <div style={{
                fontWeight: 700,
                fontSize: 14,
                color: "#0D47A1"
              }}>{v}</div>
                    <div style={{
                fontSize: 10,
                color: "#0D47A1",
                marginTop: 1
              }}>{l}</div>
                  </div>)}
              </div>
            </div>
            <div style={{
          display: "flex",
          gap: 8,
          flexShrink: 0
        }}>
              
              <button onClick={() => setShowCoordinator(true)} style={{
            background: "#FFFFFF",
            color: "#0D47A1",
            padding: "6px 14px",
            borderRadius: 30,
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
                <i className="fas fa-edit"></i> Edit
              </button>
            </div>
          </div>}
      </div>
      <style>{`
        @media (max-width: 900px) {
          .hm-co-banner-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Department Tabs ── */}
      <div style={{
      padding: "0 24px",
      marginBottom: 8
    }}>
        <div style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        paddingBottom: 12
      }}>
          {DEPTS.map((d, i) => {
          const isGroupHeader = i === 7;
          return <span key={d} style={{
            display: "contents"
          }}>
                {isGroupHeader && <span style={{
              fontSize: 10,
              color: "#0D47A1",
              fontWeight: 600,
              alignSelf: "center",
              padding: "0 4px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4
            }}>
                    <i className="fas fa-layer-group"></i> HS Groups:
                  </span>}
                <button onClick={() => setActiveDept(d)} style={{
              padding: "5px 14px",
              borderRadius: 30,
              fontSize: 12,
              cursor: "pointer",
              background: activeDept === d ? "#0D47A1" : "transparent",
              color: activeDept === d ? "#FFFFFF" : "#0D47A1",
              fontFamily: "'Poppins',sans-serif",
              fontWeight: activeDept === d ? 600 : 400,
              transition: "all 0.2s",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              boxShadow: activeDept === d ? "0 2px 8px rgba(13,71,161,0.2)" : "none"
            }}>
                  {activeDept === d ? <i className="fas fa-check-circle"></i> : ""} {d}
                </button>
              </span>;
        })}
        </div>
      </div>

      {/* ── Staff Table ── */}
      <div style={{
      margin: "0 24px 24px",
      background: "#FFFFFF",
      borderRadius: 16,
      overflowX: "auto",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
    }}>
        <table style={{
        width: "100%",
        borderCollapse: "collapse",
        minWidth: 1000,
        fontFamily: "'Poppins', sans-serif"
      }}>
          <thead style={{
          background: "#0D47A1"
        }}>
            <tr>
              {["S.No", "Name", "Department", "Role", "Experience", "Phone", "Class Advisor", "Actions"].map(h => <th key={h} style={{
              padding: "12px 16px",
              textAlign: h === "Actions" ? "center" : "left",
              fontSize: 12,
              fontWeight: 600,
              color: "#FFFFFF"
            }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map((staff, idx) => {
            const deptColor = DEPT_COLORS[staff.dept] || "#0D47A1";
            const advisedClasses = getAdvisedClasses(staff._id);
            return <tr key={staff._id} style={{
              background: idx % 2 === 0 ? "#FFFFFF" : "#E3F2FD"
            }}>
                  <td style={{
                padding: "12px 16px",
                fontSize: 13,
                color: "#0D47A1"
              }}>{idx + 1}</td>
                  <td style={{
                padding: "12px 16px",
                fontSize: 14,
                fontWeight: 500,
                color: "#0D47A1"
              }}>{staff.name}</td>
                  <td style={{
                padding: "12px 16px"
              }}>
                    <span style={{
                  background: deptColor,
                  color: "#FFFFFF",
                  padding: "4px 10px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 500
                }}>{staff.dept}</span>
                  </td>
                  <td style={{
                padding: "12px 16px",
                fontSize: 13,
                color: "#0D47A1"
              }}>{staff.role}</td>
                  <td style={{
                padding: "12px 16px",
                fontSize: 13,
                color: "#0D47A1"
              }}>{staff.exp} yrs</td>
                  <td style={{
                padding: "12px 16px",
                fontSize: 13,
                color: "#0D47A1"
              }}>{staff.phone}</td>
                  <td style={{
                padding: "12px 16px",
                fontSize: 12,
                color: "#0D47A1"
              }}>
                    {advisedClasses.length > 0 ? <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4
                }}>
                        {advisedClasses.slice(0, 2).map(c => <span key={c} style={{
                    background: "#0D47A1",
                    color: "#FFFFFF",
                    padding: "2px 8px",
                    borderRadius: 16,
                    fontSize: 11,
                    fontWeight: 500
                  }}>{c}</span>)}
                        {advisedClasses.length > 2 && <span style={{
                    background: "#0D47A1",
                    color: "#FFFFFF",
                    padding: "2px 8px",
                    borderRadius: 16,
                    fontSize: 11,
                    fontWeight: 500
                  }}>+{advisedClasses.length - 2}</span>}
                      </div> : <span style={{
                  color: "#0D47A1",
                  fontSize: 11
                }}>—</span>}
                  </td>
                  <td style={{
                padding: "12px 16px",
                textAlign: "center"
              }}>
                    <button onClick={() => setViewStaff(staff)} style={{
                  background: "#0D47A1",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#FFFFFF",
                  cursor: "pointer",
                  fontFamily: "'Poppins',sans-serif",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4
                }}>
                      <i className="fas fa-eye"></i> View
                    </button>
                  </td>
                </tr>;
          })}
            {filtered.length === 0 && <tr>
                <td colSpan="8" style={{
              padding: "48px 16px",
              textAlign: "center",
              color: "#0D47A1",
              fontSize: 14
            }}>
                  <i className="fas fa-users-slash"></i> No staff found in this department.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {/* ── Modals ── */}
      {showPrincipal && <PrincipalDetailsModal onClose={() => setShowPrincipal(false)} onEdit={() => setShowHMContact(true)} />}
      {showCoordinator && <CoordinatorModal onClose={() => setShowCoordinator(false)} onSaved={handleCoordinatorSaved} />}
      {showHMContact && <HeadmistressContactModal onClose={() => setShowHMContact(false)} onSaved={handleHMSaved} />}
      {showAdvisor && <ClassAdvisorModal staffList={staffList} advisorMap={advisorMap} onClose={() => setShowAdvisor(false)} onSave={handleSaveAdvisors} onDelete={handleDeleteAdvisor} />}
      {showAdd && <AddStaffModal onClose={() => setShowAdd(false)} onAdd={s => setStaffList(p => [s, ...p])} onUpdate={handleUpdateStaff} editingStaff={null} />}
      {showAddWarden && <AddWardenModal onClose={() => setShowAddWarden(false)} onAdd={s => setStaffList(p => [s, ...p])} />}
      {viewStaff && <ViewProfileModal staff={viewStaff} onClose={() => setViewStaff(null)} onEdit={s => {
      setEditingStaff(s);
      setViewStaff(null);
    }} advisorMap={advisorMap} />}
      {editingStaff && <AddStaffModal onClose={() => setEditingStaff(null)} onAdd={s => setStaffList(p => [s, ...p])} onUpdate={handleUpdateStaff} editingStaff={editingStaff} />}
    </div>;
}